import { type SelectionState } from '../../machine/state/types.ts';
import { createEffect } from '../utils/create-effect.ts';

export interface ColumnSelectionPingEffectDeps {
  resolveColumnIndexToFieldId: (columnIndex: number) => string | undefined;
  notifyColumnSelectionChanged: (fieldId: string) => void;
}

interface ColumnSelectionSnapshot {
  fieldIdsByIndex: Map<number, string>;
  selectedIndices: Set<number>;
}

const haveSameSelectedColumns = (
  previousSnapshot: ColumnSelectionSnapshot,
  nextSnapshot: ColumnSelectionSnapshot
): boolean => {
  if (previousSnapshot.selectedIndices.size !== nextSnapshot.selectedIndices.size) {
    return false;
  }

  for (const index of previousSnapshot.selectedIndices) {
    if (
      !nextSnapshot.selectedIndices.has(index) ||
      previousSnapshot.fieldIdsByIndex.get(index) !==
        nextSnapshot.fieldIdsByIndex.get(index)
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Invalidates field headers when selected membership or index-to-ID mapping changes.
 *
 * The effect observes completed selection replacements and modifications, resolves
 * selected column indices through the injected adapter, and emits field-scoped
 * invalidations for every previously or currently selected field. Presentation
 * layouts remain the sole owners of their adjacency and range-edge derivation.
 * Current occupants of affected indices are also invalidated so an adapter remap
 * cannot leave displaced or moved headers stale. Work stays bounded by the previous
 * and current selected columns. The cache belongs to one effect instance and is
 * discarded with the recyclable selections machine; it installs no listeners and
 * needs no explicit setup or disposal.
 */
export const createColumnSelectionPingEffect = createEffect(
  ({
    resolveColumnIndexToFieldId,
    notifyColumnSelectionChanged
  }: ColumnSelectionPingEffectDeps) => {
    let previousSnapshot: ColumnSelectionSnapshot = {
      fieldIdsByIndex: new Map(),
      selectedIndices: new Set()
    };

    const getColumnSelectionSnapshot = (
      state: SelectionState
    ): ColumnSelectionSnapshot => {
      const selectedIndices = new Set<number>();

      if (state.mode !== 'column') {
        return {
          fieldIdsByIndex: new Map(),
          selectedIndices
        };
      }

      for (const range of state.selections) {
        const start = Math.min(range.anchor.col.index, range.lead.col.index);
        const end = Math.max(range.anchor.col.index, range.lead.col.index);

        for (let index = start; index <= end; index += 1) {
          selectedIndices.add(index);
        }
      }

      const fieldIdsByIndex = new Map<number, string>();

      for (const index of selectedIndices) {
        const fieldId = resolveColumnIndexToFieldId(index);

        if (fieldId === undefined) {
          continue;
        }

        fieldIdsByIndex.set(index, fieldId);
      }

      return { fieldIdsByIndex, selectedIndices };
    };

    return {
      deltaTypes: ['selectionReplaced', 'selectionModified'],

      onDelta(_delta, state) {
        const nextSnapshot = getColumnSelectionSnapshot(state);

        if (haveSameSelectedColumns(previousSnapshot, nextSnapshot)) {
          previousSnapshot = nextSnapshot;

          return;
        }

        const fieldIdsToNotify = new Set([
          ...previousSnapshot.fieldIdsByIndex.values(),
          ...nextSnapshot.fieldIdsByIndex.values()
        ]);

        const selectedIndices = new Set([
          ...previousSnapshot.selectedIndices,
          ...nextSnapshot.selectedIndices
        ]);

        for (const index of selectedIndices) {
          const previousFieldId = previousSnapshot.fieldIdsByIndex.get(index);
          const currentFieldId = nextSnapshot.selectedIndices.has(index)
            ? nextSnapshot.fieldIdsByIndex.get(index)
            : resolveColumnIndexToFieldId(index);

          if (currentFieldId === previousFieldId) {
            continue;
          }

          if (previousFieldId !== undefined) {
            fieldIdsToNotify.add(previousFieldId);
          }

          if (currentFieldId !== undefined) {
            fieldIdsToNotify.add(currentFieldId);
          }
        }

        for (const fieldId of fieldIdsToNotify) {
          notifyColumnSelectionChanged(fieldId);
        }

        previousSnapshot = nextSnapshot;
      }
    };
  }
);
